import { useCallback, useEffect, useRef, useState } from "react";

export type CopyFeedbackStatus = "success" | "error";

export interface CopyFeedbackState {
  status: CopyFeedbackStatus;
  message: string;
}

export type CopyFeedbackHandler = (
  status: CopyFeedbackStatus,
  message: string,
) => void;

export function useCopyFeedback(timeoutMs = 2200) {
  const [feedback, setFeedback] = useState<CopyFeedbackState | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerFeedback = useCallback<CopyFeedbackHandler>(
    (status, message) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setFeedback({ status, message });
      timeoutRef.current = setTimeout(() => {
        setFeedback(null);
      }, timeoutMs);
    },
    [timeoutMs],
  );

  const clearFeedback = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setFeedback(null);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    feedback,
    triggerFeedback,
    clearFeedback,
  };
}

